import React, {useEffect, useState} from 'react';
import {Form} from 'react-bootstrap';
import {
	ComponentUtils,
	connect,
	DetailActions,
	getRouter,
	notification,
	OrganizationSelection,
	RenderUtils,
	SwEmbeddedDetail,
	SwForm,
	SwList,
	SwPortlet,
	SwProgressBar,
	UniversalCodebookSelection
} from 'simplifyworks-core-ui';
import {FaSearch} from 'react-icons/fa/';
import i18n from 'i18next';
import {FOEDUS_ROLE, organOfferIdByEnv} from '../../domain/constants';
import {useNewOrganOfferFilters} from '../../hooks/useNewOrganOfferFilters';
import {
	CountryWithFlag,
	IndeterminateCheckbox,
	OrganizationsList,
	SelectedOrganizationsList
} from '../renderers/renderers';
import {useOrganOfferHandlers} from '../../hooks/useOrganOfferHandlers';
import {bindActionCreators} from 'redux';
import {getOrganOfferRecipientsInitialValue} from '../../rest';

const FoedusNewOrganOfferPortlet = (props) => {
	const {
		loggedUserPrimaryFoedusRole,
		loggedUserCountry,
		nationalBodyCoreCodebook,
		countryCoreCodebook,
		newOrganOfferFormData
	} = props;

	const [notificationGroupsInitialValue, setNotificationGroupsInitialValue] = useState(null);

	const { organizationFixedFilter, countryFixedFilter } = useNewOrganOfferFilters({
		loggedUserPrimaryFoedusRole: props.loggedUserPrimaryFoedusRole,
		loggedUserCountry: props.loggedUserCountry,
		nationalBodyCoreCodebook: nationalBodyCoreCodebook,
		countryCoreCodebook: countryCoreCodebook,
		newOrganOfferFormData: newOrganOfferFormData
	});

	const changeSelectedOrganizationsValue = (value) => {
		props.actions.detail.changeValue(
			'/api/foedus/portlet/new-organ-offer/new',
			'DEFAULT',
			'selectedOrganizations',
			value
		);
	};

	useEffect(() => {
		getOrganOfferRecipientsInitialValue(
			{},
			(res) => {
				const allSuitableOrganizations = res?.organizations?.resources ?? [];

				setNotificationGroupsInitialValue({
					countries: res?.countries?.resources,
					organizations: allSuitableOrganizations,
					globalGroups: res?.globalGroups?.map(globalGroup => {
						return {
							...globalGroup,
							organizations: globalGroup?.organizations?.filter(ggOrg => allSuitableOrganizations?.some(org => org?.code === ggOrg?.code))
						};
					}),
					privateGroups: res?.privateGroups?.map(privateGroup => {
						return {
							...privateGroup,
							organizations: privateGroup?.organizations?.filter(pgOrg => allSuitableOrganizations?.some(org => org?.code === pgOrg?.code))
						};
					})
				});
			},
			(err) => {
				notification.showLocalizedMessage({
					level: 'error',
					titleKey: 'unexpected-error',
					messageKey: err.data.messageKey
				});
			}
		);
	}, []);

	const {
		isFoedusAdmin,
		onCheckedAllChange,
		onCheckedAllGlobalGroupsChange,
		onCheckedAllPrivateGroupsChange,
		onCountryCheckedChange,
		onOrganizationCheckedChange,
		onGlobalGroupCheckedChange,
		onPrivateGroupCheckedChange,
		getSelectedOrganizationsInCountryCount,
		getTotalOrganizationsInCountryCount,
		getSelectedOrganizationsInGlobalGroupCount,
		getTotalOrganizationsInGlobalGroupCount,
		getSelectedOrganizationsInPrivateGroupCount,
		getTotalOrganizationsInPrivateGroupCount
	} = useOrganOfferHandlers({
		newOrganOfferFormData: props?.newOrganOfferFormData,
		changeSelectedOrganizationsValue,
		loggedUserPrimaryFoedusRole: props?.loggedUserPrimaryFoedusRole
	});

	if (!loggedUserCountry || !loggedUserPrimaryFoedusRole) {
		return null;
	}

	return (
		<SwPortlet
			parentProps={props}
			parentState={{notificationGroupsInitialValue}}
			render={() => (
				<>
					{notificationGroupsInitialValue
						? (
							<SwEmbeddedDetail
								name={'portlet-for-' + loggedUserPrimaryFoedusRole}
								rest='/api/foedus/portlet/new-organ-offer'
								restId='new'
								createButtonLabelKey='foedus-create'
								removable={false}
								exitPath='/ood/entries'
								newCallback={(_, ids) => {
									if (!ids) {
										return null;
									}

									if (ids.length > 1) {
										getRouter().navigate('/ood/entries-by-definition/' + organOfferIdByEnv[globalParams.hostName]);
									} else {
										getRouter().navigate('/ood/entries/' + ids + '/definition/' + organOfferIdByEnv[globalParams.hostName]);
									}
								}}
							>

								<SwForm
									initialValue={{
										selectedOrganizations: [],
										notificationGroups: notificationGroupsInitialValue
									}}
									valueChanged={(data, property, value, callback) => {

										// Ak foedus admin upravi country, tak vynuluujem organization
										if (property === 'values.COUNTRY.item' && data?.values?.ORG?.item && isFoedusAdmin) {
											data.values.ORG.item = null;
										}

										callback(data);
									}}
								>
									<SwForm.Section
										className={'mt-3'}
									/>

									<SwForm.Section
										type='collapsible-panel'
										defaultExpanded
										label={(
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													fontSize: '14px',
													transform: 'translateY(1px)'
												}}
											>
												{i18n.t('foedus-new-organ-offer-base-info')}
											</div>
										)}

									>
										<SwForm.EntitiesSelect
											size={12}
											property='values.ORGANS.items'
											rest='/api/ood/codebooks'
											name='ORGANS'
											validations='required'
											labelKey='foedus-organs'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusOrganTypes'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.Number
											size={6}
											property='values.HEIGHT.number'
											validations='required;numericRange(20, 210)'
											labelKey='foedus-height'
										/>
										<SwForm.Number
											size={6}
											property='values.WEIGHT.number'
											validations='required;numericRange(2, 200)'
											labelKey='foedus-weight'
										/>

										<SwForm.Number
											size={3}
											property='values.AGE.number'
											validations='required;numericRange(0, 100)'
											labelKey='foedus-age'
										/>

										<SwForm.EntitySelect
											size={3}
											property='values.AGE_TYPE.item'
											rest='/api/ood/codebooks'
											name='AGE_TYPE'
											validations='required'
											labelKey='foedus-ageType'
											pageSize={25}
											fixedFilter={{
												codebookType: 'Ood_age_type'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.EntitySelect
											size={6}
											property='values.GENDER.item'
											rest='/api/ood/codebooks'
											name='GENDER'
											validations='required'
											labelKey='foedus-gender'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusGender'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.EntitySelect
											size={6}
											property='values.AB0.item'
											rest='/api/ood/codebooks'
											name='AB0'
											validations='required'
											labelKey='foedus-ab0'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusBloodGroups'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.EntitySelect
											size={6}
											property='values.RH.item'
											rest='/api/ood/codebooks'
											name='RH'
											validations='required'
											labelKey='foedus-rh'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusRh'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.EntitySelect
											size={6}
											property='values.DONOR_TYPE.item'
											rest='/api/ood/codebooks'
											name='DONOR_TYPE'
											validations='required'
											labelKey='foedus-donorType'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusDonorType'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
										/>

										<SwForm.EntitySelect
											size={6}
											property='values.NRP.item'
											rest='/api/ood/codebooks'
											name='NRP'
											validations='required'
											labelKey='foedus-nrp'
											pageSize={25}
											fixedFilter={{
												codebookType: 'OodFoedusNRP'
											}}
											render={UniversalCodebookSelection.renderText}
											searchProperties={['name', 'code']}
											defaultOrdering={[{property: 'rank', order: 'asc'}]}
											writingEnabled
											hidden={(!props?.newOrganOfferFormData?.values?.DONOR_TYPE?.item || props?.newOrganOfferFormData?.values?.DONOR_TYPE?.item?.code === 'DBD')}
										/>

										<SwForm.EntitySelect
											size={12}
											property='values.COUNTRY.item'
											validations='required'
											name='COUNTRY'
											labelKey='foedus-country'
											render={OrganizationSelection.renderText}
											rest='/api/core/organizations'
											preSelect
											fixedFilter={countryFixedFilter}
											hidden={!isFoedusAdmin}
										/>

										<SwForm.EntitySelect
											size={12}
											property='values.ORG.item'
											name={'ORG'}
											labelKey='foedus-organization'
											validations='required'
											render={OrganizationSelection.renderText}
											rest='/api/core/organizations'
											preSelect
											disabled={isFoedusAdmin && !props?.newOrganOfferFormData?.values?.COUNTRY?.item}
											fixedFilter={organizationFixedFilter}
											hidden={!loggedUserCountry || loggedUserPrimaryFoedusRole === FOEDUS_ROLE.COORDINATOR}
										/>

										<SwForm.String
											size={12}
											property='values.NOTE.text'
											labelKey='foedus-note'
											type='area'
											rowsExpanded={5}
										/>

										<SwForm.Attachments
											size={12}
											property='attachments'
											labelKey='oodEnt-attachmentsAll'
											multiple={false}
											rest='/api/ood/attachments'>
											<SwForm.EmbeddableForm name='oodAtt'>
												<SwForm.Section>
													<SwForm.String size={12} property='name' disabled/>
													<SwForm.String size={12} property='guid' disabled/>
													<SwForm.String size={6} property='size' disabled/>
													<SwForm.Datetime size={6} property='uploaded' timeEnabled disabled/>
													<SwForm.EntitySelect
														size={12}
														property='type'
														rest='/api/core/codebooks'
														name='attachmentTypeCb'
														validations='required'
														preSelect={true}
														pageSize={25}
														fixedFilter={{
															codebookType: 'OodAttachmentTypeCodebook'
														}}
														render={UniversalCodebookSelection.renderText}
														searchProperties={['name', 'code']}
														defaultOrdering={[{property: 'rank', order: 'asc'}]}
													/>
													<SwForm.String size={12} property='description' type='area' rows={3} validations='length(255)'/>

												</SwForm.Section>
											</SwForm.EmbeddableForm>

											<SwForm.AttachmentsList name='ood-attachment'>
												<SwList.Table
													labelKeyPrefix='oodAtt'
													getDeleteValueLabel={resource => resource?.name}
												>
													<SwList.Custom
														property='detail'
														pathProperty='id'
														labelKey='oodAtt-detail'
														width='60px'
														orderable={false}
														render={() => (
															<span className='modal-detail-link'>
																<FaSearch style={{marginBottom: '3px'}}/>
															</span>
														)}
													/>
													<SwList.Attachment
														property='name'
														rest='/api/ood/attachments'
														width='200px'
													/>
													<SwList.Custom
														property='size'
														width='100px'
														render={(attachment) => <div style={{
															textAlign: 'right',
															whiteSpace: 'nowrap'
														}}
														>
															{RenderUtils.getFileSize(attachment.size, 1)}
														</div>}
														orderable={false}>
													</SwList.Custom>
													<SwList.Entity
														property='type'
														width='100px'
														orderBy={'name.multilangEntries.' + i18n.language}
														render={f => f && f.name?.multilangEntries && RenderUtils.renderMultilangProperty(f, 'name')}
													>
													</SwList.Entity>
													<SwList.Datetime property='uploaded' timeEnabled={true} width='100px'/>
												</SwList.Table>
												<SwList.Info/>
											</SwForm.AttachmentsList>
										</SwForm.Attachments>

									</SwForm.Section>

									<SwForm.Section
										type='collapsible-panel'
										defaultExpanded
										label={(
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
													fontSize: '14px',
													transform: 'translateY(1px)'
												}}
											>
												{i18n.t('foedus-new-organ-offer-recipients')}
											</div>
										)}
									>

										<SwForm.Section>

											<SwForm.Boolean
												property='values.NOTIFY_ALL.bool'
												labelKey='foedus-notifyAll'
												size={12}
												showNull={false}
											/>

											<SwForm.Section type='tabs' labelKey='foedus-countries'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.countries'
													label=' '
												>

													<SwForm.EmbeddablesList
														name='countries'
													>
														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allCountriesSelected = props?.newOrganOfferFormData?.notificationGroups?.countries?.every(country =>
																		getSelectedOrganizationsInCountryCount(country) === getTotalOrganizationsInCountryCount(country));
																	const someCountriesSelected = props?.newOrganOfferFormData?.notificationGroups?.countries?.some(country =>
																		getSelectedOrganizationsInCountryCount(country) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allCountriesSelected}
																			indeterminate={someCountriesSelected && !allCountriesSelected}
																			onChange={onCheckedAllChange}
																		/>
																	);
																}}
																render={(resource) => {
																	const allOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) === getTotalOrganizationsInCountryCount(resource) && getTotalOrganizationsInCountryCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInCountryCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(e) => onCountryCheckedChange(resource, e.target.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='country'
																labelKey='foedus-country'
																render={resource => {
																	return (
																		<div className="d-flex align-items-center">
																			<CountryWithFlag
																				code={resource?.code}
																				name={resource?.nameEng ?? resource?.name}
																			/>
																			&nbsp;{getSelectedOrganizationsInCountryCount(resource)} / {getTotalOrganizationsInCountryCount(resource)}
																		</div>

																	);
																}}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => {
																	return (
																		<div className="d-flex align-items-center">
																			{props?.newOrganOfferFormData?.selectedOrganizations
																				?.filter(so => so?.organization?.code === resource?.code)
																				?.map((o, index, array) => o?.name + (index < array.length - 1 ? ', ' : ''))}
																		</div>
																	);
																}}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='core-organizations'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.organizations'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='organizations'
														defaultOrdering={[{
															property: 'organization.name',
															order: 'asc'
														}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => (
																	<Form.Check
																		checked={props?.newOrganOfferFormData?.selectedOrganizations?.length === props?.newOrganOfferFormData?.notificationGroups?.organizations?.length}
																		onChange={onCheckedAllChange}
																	/>
																)}
																render={resource => {
																	return (
																		<Form.Check
																			checked={props?.newOrganOfferFormData?.selectedOrganizations?.some(so => so?.code === resource?.code)}
																			onChange={e => onOrganizationCheckedChange(resource, e?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='country'
																labelKey='foedus-country'
																render={resource => {
																	return (
																		<>
																			<div className="d-flex align-items-center">
																				<CountryWithFlag
																					code={resource?.organization?.code}
																					name={resource?.organization?.nameEng ?? resource?.organization?.name}
																				/>
																			</div>
																		</>
																	);
																}}
															/>

															<SwList.Custom
																property='organization'
																labelKey='core-organization'
																render={resource => resource?.nameEng ?? resource?.name}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>
												</SwForm.Embeddables>

											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='foedus-global-groups'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.globalGroups'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='globalGroups'
														defaultOrdering={[{
															property: 'organization.name',
															order: 'asc'
														}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allGlobalGroupsSelected = props?.newOrganOfferFormData?.notificationGroups?.globalGroups?.every(globalGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(globalGroup) === getTotalOrganizationsInGlobalGroupCount(globalGroup));
																	const someGlobalGroupSelected = props?.newOrganOfferFormData?.notificationGroups?.globalGroups?.some(globalGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(globalGroup) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allGlobalGroupsSelected}
																			indeterminate={someGlobalGroupSelected && !allGlobalGroupsSelected}
																			onChange={onCheckedAllGlobalGroupsChange}
																		/>
																	);
																}}
																render={resource => {
																	const allOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) === getTotalOrganizationsInGlobalGroupCount(resource) && getTotalOrganizationsInGlobalGroupCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInGlobalGroupCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(event) => onGlobalGroupCheckedChange(resource, event?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='name'
																labelKey='foedus-name'
																render={resource => (
																	<div className="d-flex align-items-center">
																		{resource?.name}
																		&nbsp;{getSelectedOrganizationsInGlobalGroupCount(resource)} / {getTotalOrganizationsInGlobalGroupCount(resource)}
																	</div>
																)}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => <OrganizationsList
																	organizations={resource?.organizations}/>}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>

											<SwForm.Section type='tabs' labelKey='foedus-private-groups'>
												<SwForm.Embeddables
													size={12}
													property='notificationGroups.privateGroups'
													label=' '
												>
													<SwForm.EmbeddablesList
														name='privateGroups'
														defaultOrdering={[{
															property: 'organization.name',
															order: 'asc'
														}]}
													>

														<SwList.Table>
															<SwList.Custom
																width='26px'
																property='checked'
																label=' '
																orderable={false}
																renderHeaderCell={() => {
																	const allPrivateGroupsSelected = props?.newOrganOfferFormData?.notificationGroups?.privateGroups?.every(privateGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(privateGroup) === getTotalOrganizationsInGlobalGroupCount(privateGroup));
																	const somePrivateGroupSelected = props?.newOrganOfferFormData?.notificationGroups?.privateGroups?.some(privateGroup =>
																		getSelectedOrganizationsInGlobalGroupCount(privateGroup) > 0);

																	return (
																		<IndeterminateCheckbox
																			checked={allPrivateGroupsSelected}
																			indeterminate={somePrivateGroupSelected && !allPrivateGroupsSelected}
																			onChange={onCheckedAllPrivateGroupsChange}
																		/>
																	);
																}}
																render={resource => {
																	const allOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) === getTotalOrganizationsInPrivateGroupCount(resource) && getTotalOrganizationsInPrivateGroupCount(resource) !== 0;
																	const someOrganizationsSelected = getSelectedOrganizationsInPrivateGroupCount(resource) > 0;

																	return (
																		<IndeterminateCheckbox
																			checked={allOrganizationsSelected}
																			indeterminate={someOrganizationsSelected && !allOrganizationsSelected}
																			onChange={(event) => onPrivateGroupCheckedChange(resource, event?.target?.checked)}
																		/>
																	);
																}}
															/>
															<SwList.Custom
																property='name'
																labelKey='foedus-name'
																render={resource => (
																	<div className="d-flex align-items-center">
																		{resource?.name}
																		&nbsp;{getSelectedOrganizationsInPrivateGroupCount(resource)} / {getTotalOrganizationsInPrivateGroupCount(resource)}
																	</div>
																)}
															/>

															<SwList.Custom
																property='organizations'
																labelKey='core-organizations'
																render={resource => <OrganizationsList
																	organizations={resource?.organizations}/>}
															/>
														</SwList.Table>
													</SwForm.EmbeddablesList>

												</SwForm.Embeddables>
											</SwForm.Section>

										</SwForm.Section>

										<SwForm.Section>
											<SwForm.Custom
												property='selectedOrganizations'
												labelKey='foedus-selected-organizations'
												render={(resource) => <SelectedOrganizationsList
													selectedOrganizations={resource?.selectedOrganizations}/>}
											/>
										</SwForm.Section>

									</SwForm.Section>
								</SwForm>
							</SwEmbeddedDetail>
						)
						: <SwProgressBar centered size='md'/>}
				</>
			)}
		/>
	);
};

const mapStateToProps = (state) => {
	const newOrganOfferFormData = ComponentUtils.getFormData(state, '/api/foedus/portlet/new-organ-offer/new', 'DEFAULT');
	return {
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole,
		loggedUserCountry: state?.foedus?.loggedUserCountry,
		organOfferAttachmentTypeCbIds: state?.foedus?.organOfferAttachmentTypeCbIds,
		countryCoreCodebook: state?.foedus?.countryCoreCodebook,
		nationalBodyCoreCodebook: state?.foedus?.nationalBodyCoreCodebook,
		newOrganOfferFormData
	};
};

const mapDispatchToProps = (dispatch) => ({
	dispatch,
	actions: {
		detail: bindActionCreators(DetailActions, dispatch)
	}
});


export default connect(mapStateToProps, mapDispatchToProps)(FoedusNewOrganOfferPortlet);
